import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

function Hero({ settings, featuredPackages = [], isMenuOpen = false, toggleMenu = () => {} }) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'bs';
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);

  // Dynamic texts from settings or defaults
  const heroTitle = currentLang === 'bs' 
    ? (settings?.heroTitleBs || 'Timeless stories') 
    : (settings?.heroTitleEn || 'Timeless stories');
  
  const heroSubtitle = currentLang === 'bs' 
    ? (settings?.heroSubtitleBs || 'Wedding photography & films') 
    : (settings?.heroSubtitleEn || 'Wedding photography & films');
  
  const heroMotto = currentLang === 'bs' 
    ? (settings?.heroMottoBs || 'Allowing your real moments to be felt, remembered, and lived again') 
    : (settings?.heroMottoEn || 'Allowing your real moments to be felt, remembered, and lived again');

  const overlayOpacity = settings?.heroOverlayOpacity || 0.1;

  const heroImages = settings?.heroImages && settings.heroImages.length > 0
    ? settings.heroImages
    : [
        '/img/photos/hero1d.jpg',
        '/img/photos/hero2d.jpg',
        '/img/photos/hero3d.jpg'
      ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <section className="relative w-full h-screen overflow-hidden bg-gray-100">
      {/* Background Image Slider */}
      <div className="absolute inset-0">
        {heroImages.map((img, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-[2000ms] ease-in-out ${
              idx === currentHeroIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={img}
              alt={`Nejira Zukić - Profesionalna fotografija vjenčanja ${idx + 1} - Elegantan pristup vašem najvažnijem danu`}
              className="w-full h-full object-cover grayscale opacity-90"
            />
            {/* Dynamic Overlay */}
            <div 
              className="absolute inset-0 bg-black transition-all duration-700"
              style={{ opacity: overlayOpacity }}
            ></div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-8 md:px-16 lg:px-20 py-8 md:py-10">
        <Link to="/" className={`text-xl md:text-2xl tracking-[0.4em] font-light transition-colors duration-700 ${overlayOpacity > 0.4 ? 'text-white' : 'text-gray-900'}`}>
          NEJIRA ZUKIĆ
        </Link>
        
        {/* Navigation & Menu Button */}
        <div className="flex items-center space-x-8">
          <button 
            onClick={toggleMenu} 
            className={`text-[10px] md:text-xs tracking-[0.3em] font-bold uppercase transition-all duration-300 hover:opacity-60 ${overlayOpacity > 0.4 ? 'text-white' : 'text-gray-900'}`}
          >
            {isMenuOpen ? (currentLang === 'bs' ? 'ZATVORI' : 'CLOSE') : 'MENU'}
          </button>
        </div>
      </div>

      {/* Center Content */}
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <div className="text-center px-6 md:px-12 max-w-6xl">
          <h2 className={`text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-serif mb-4 md:mb-6 leading-tight tracking-wide transition-colors duration-700 ${overlayOpacity > 0.4 ? 'text-white' : 'text-gray-900'}`}>
            {heroTitle}
          </h2>
          <h3 className={`text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-serif mb-6 md:mb-10 leading-tight tracking-wide italic transition-colors duration-700 ${overlayOpacity > 0.4 ? 'text-white/90' : 'text-gray-900'}`}>
            {heroSubtitle}
          </h3>
          <p className={`text-xs md:text-sm lg:text-base tracking-[0.3em] font-light uppercase max-w-2xl mx-auto leading-loose transition-colors duration-700 ${overlayOpacity > 0.4 ? 'text-white/80' : 'text-gray-800'}`}>
            {heroMotto}
          </p>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20">
        <div className={`w-px h-16 bg-gradient-to-b from-transparent transition-all duration-700 ${overlayOpacity > 0.4 ? 'to-white' : 'to-gray-900'}`}></div>
      </div>
    </section>
  );
}

export default Hero;
